import { Paper, TextField, Typography } from "@mui/material";
import { Send } from "@mui/icons-material";
import moment from "moment-timezone";
import React, { useEffect, useMemo, useState } from "react";
import { t } from "ttag";
import { GlobalAdmin as SdkGlobalAdmin } from "coolremote-sdk";
import CoolTable, { IHeadCell } from "../../components/CoolTable/CoolTable";
import { useStoreActions } from "../../models/RootStore";
import ServiceNavigationBar from "../../widgets/Menu/ServiceNavigationBar";
import SendPushNotificationDialog from "./SendPushNotificationDialog";
import useStyles from "./PushTokens.style";

type Order = "asc" | "desc";

interface IPushTokenUser {
  id: string;
  username?: string;
  email?: string;
}

interface IPushCredential {
  installationId: string;
  platform: string;
  appType?: string;
  user: IPushTokenUser | null;
  lastSeenAt: string;
  createdAt: string;
}

// CoolTable keys selection off row.id; the credential response has no id of
// its own, but installationId is already unique (unique index in Mongo).
interface IPushToken extends IPushCredential {
  id: string;
}

// Matches the list endpoint's validator max (validators/adminsPushNotifications.js
// on orchastrator) - one page covers this ticket's expected row counts; real
// pagination can follow if that assumption breaks.
const MAX_PAGE_SIZE = 200;

function stableSort<T>(array: T[], comparator: (a: T, b: T) => number) {
  const stabilizedThis = array.map((el, index) => [el, index] as [T, number]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
  if (b[orderBy] < a[orderBy]) return -1;
  if (b[orderBy] > a[orderBy]) return 1;
  return 0;
}

function getComparator<T>(order: Order, orderBy: keyof T): (a: T, b: T) => number {
  return order === "desc"
    ? (a, b) => descendingComparator<T>(a, b, orderBy)
    : (a, b) => -descendingComparator<T>(a, b, orderBy);
}

const tableColumns: IHeadCell<IPushToken>[] = [
  { id: "installationId", title: t`Installation ID` },
  { id: "platform", title: t`Platform`, width: "10ch" },
  { id: "appType", title: t`App`, width: "12ch" },
  { id: "user", title: t`User` },
  { id: "lastSeenAt", title: t`Last Seen`, width: "22ch" },
  { id: "createdAt", title: t`Registered`, width: "22ch" }
];

const PushTokens = (props: any) => {
  const styles: any = useStyles();
  const addMessage = useStoreActions((actions) => actions.errorMessage.addMessage);

  const [tokens, setTokens] = useState<IPushToken[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [sending, setSending] = useState(false);
  // Bumped after a successful send to force CoolTable to remount: it owns
  // row-selection as private internal state with no way to reset it from
  // outside, so a stale check-mark would otherwise survive a send.
  const [tableKey, setTableKey] = useState(0);

  const loadTokens = () => {
    SdkGlobalAdmin.getPushCredentials({ pageNum: 1, pageSize: MAX_PAGE_SIZE })
      .then((resp: { credentials: IPushCredential[] }) => {
        const withIds = (resp.credentials || []).map((credential) => ({
          ...credential,
          id: credential.installationId
        }));
        setTokens(withIds);
      })
      .catch((err: any) => {
        addMessage({ message: err?.message || t`Failed to load push tokens` });
      });
  };

  useEffect(() => {
    loadTokens();
  }, []);

  const filteredTokens = useMemo(() => {
    if (!searchTerm) return tokens;
    const lowerTerm = searchTerm.toLowerCase();
    return tokens.filter((token) => {
      const installationMatch = token.installationId?.toLowerCase().includes(lowerTerm);
      const platformMatch = token.platform?.toLowerCase().includes(lowerTerm);
      const usernameMatch = token.user?.username?.toLowerCase().includes(lowerTerm);
      const emailMatch = token.user?.email?.toLowerCase().includes(lowerTerm);
      return installationMatch || platformMatch || usernameMatch || emailMatch;
    });
  }, [tokens, searchTerm]);

  const getTokensPage = (page: number, rowsPerPage: number, order: Order, orderBy: keyof IPushToken) => {
    return stableSort<IPushToken>(filteredTokens, getComparator(order, orderBy)).slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage
    );
  };

  const renderCell = (row: IPushToken, id: keyof IPushToken) => {
    switch (id) {
      case "appType":
        return row.appType || "—";
      case "user":
        return row.user?.username || row.user?.email || "—";
      case "lastSeenAt":
      case "createdAt":
        return row[id] ? moment(row[id]).format("llll") : "—";
      default:
        return row[id];
    }
  };

  const onSend = async (values: { header: string; body: string }) => {
    setSending(true);
    try {
      const result: any = await SdkGlobalAdmin.sendPushNotifications(
        selectedIds,
        values.body,
        values.header
      );
      addMessage({
        message: t`${result.successful} of ${result.recipients} devices notified`
      });
      setDialogOpen(false);
      setSelectedIds([]);
      setTableKey((key) => key + 1);
    } catch (err: any) {
      addMessage({ message: err?.message || t`Failed to send push notification` });
    } finally {
      setSending(false);
    }
  };

  return (
    <ServiceNavigationBar title={t`Push Tokens`} {...props}>
      <div className={styles.addButtonContainer}>
        <TextField
          placeholder={t`Search installation ID, platform, or user`}
          value={searchTerm}
          onChange={(e: any) => setSearchTerm(e.target.value)}
          classes={{ root: styles.searchField }}
        />
      </div>
      {tokens.length === 0 ? (
        <Typography>{t`No registered push tokens.`}</Typography>
      ) : (
        <Paper elevation={0} className={styles.paperPadding}>
          <CoolTable<IPushToken>
            key={tableKey}
            headCells={tableColumns}
            rows={filteredTokens}
            getPageRows={getTokensPage}
            renderCell={renderCell}
            defaultOrderBy={"lastSeenAt"}
            onSelectionChange={setSelectedIds}
            options={{
              withCheckboxes: true,
              withToolbar: true,
              classNameForTableContainer: styles.tableContainer,
              toolbarTitle: t`Push Tokens`,
              toolbarAction: {
                icon: <Send />,
                tooltip: t`Send test push`,
                onClick: () => setDialogOpen(true)
              }
            }}
          />
        </Paper>
      )}
      {dialogOpen && (
        <SendPushNotificationDialog
          recipientCount={selectedIds.length}
          sending={sending}
          onClose={() => setDialogOpen(false)}
          onSend={onSend}
        />
      )}
    </ServiceNavigationBar>
  );
};

export default PushTokens;
